import React, { useEffect } from "react";
import { Loader2, Inbox, CheckCircle2, AlertCircle, X } from "lucide-react";

const TOAST_DURATION_MS = 4000;

export function Spinner({ className = "" }) {
  return <Loader2 className={`w-4 h-4 spin ${className}`} aria-hidden />;
}

export function PageLoader({ label = "Loading" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-muted" role="status">
      <Loader2 className="w-6 h-6 spin mb-3 text-teal" aria-hidden />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, description, action, testId }) {
  return (
    <div className="card p-8 text-center flex flex-col items-center fade-in" data-testid={testId}>
      <div className="w-12 h-12 rounded-full bg-teal-light flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-teal" aria-hidden />
      </div>
      <h3 className="font-display text-lg text-ink mb-1">{title}</h3>
      {description && <p className="text-sm text-muted max-w-sm mb-4">{description}</p>}
      {action}
    </div>
  );
}

const STATUS_STYLES = {
  success: "bg-emerald-light text-emerald",
  running: "bg-teal-light text-teal",
  pending: "bg-amber-light text-amber",
  failed: "bg-rose-light text-rose",
  cancelled: "bg-stone-border/50 text-muted",
  active: "bg-emerald-light text-emerald",
  trialing: "bg-teal-light text-teal",
  inactive: "bg-amber-light text-amber",
  cancelled_sub: "bg-rose-light text-rose",
};

export function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-stone-border/50 text-muted";
  return (
    <span className={`chip ${cls}`} data-testid={`status-${status}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {String(status).replace("_", " ")}
    </span>
  );
}

export function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  const Icon = toast.type === "error" ? AlertCircle : CheckCircle2;
  const color = toast.type === "error" ? "text-rose" : "text-emerald";
  return (
    <div
      className="fixed z-50 bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-md card px-4 py-3 flex items-start gap-3 fade-in"
      role="alert"
      data-testid="toast"
    >
      <Icon className={`w-5 h-5 mt-0.5 ${color}`} aria-hidden />
      <p className="text-sm text-ink flex-1">{toast.message}</p>
      <button onClick={onClose} aria-label="Dismiss" data-testid="toast-close" className="text-muted hover:text-ink">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function useToast() {
  const [toast, setToast] = React.useState(null);
  const show = React.useCallback((message, type = "success") => setToast({ message, type }), []);
  const close = React.useCallback(() => setToast(null), []);
  return { toast, show, close };
}
